import { Controller } from "@hotwired/stimulus"

// "YYYY-MM-DD" de una fecha local. No usar toISOString(): da el día en UTC.
function formatLocalDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

// Corre la fecha un período para atrás (-1) o para adelante (1). El mensual va al
// día 1: con setMonth, un 31 de marzo menos un mes da 3 de marzo y no cambiaba de mes.
function shiftDate(date, period, direction) {
  if (period === 'daily') {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + direction)
  } else if (period === 'weekly') {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 7 * direction)
  }
  return new Date(date.getFullYear(), date.getMonth() + direction, 1)
}

export default class extends Controller {
  static targets = ["overlay", "periodButton", "dateButton"]
  static values = { currentPeriod: String, currentDate: String }

  connect() {
    // Clean up spinner on page load (handles back button case)
    this.hideLoading()
    this.isLoading = false

    // Listen for page show event (handles back button navigation)
    window.addEventListener('pageshow', this.handlePageShow.bind(this))

    // Configurar formateo de fecha para vista diaria
    this.setupDateFormatter()
  }

  disconnect() {
    // Clean up event listener when controller is disconnected
    window.removeEventListener('pageshow', this.handlePageShow.bind(this))

    // Clean up download listeners if they exist
    if (this.downloadCleanup) {
      this.downloadCleanup()
      this.downloadCleanup = null
    }
  }

  handlePageShow() {
    // Clean up spinner when page is shown (including back button)
    this.hideLoading()
    this.isLoading = false
  }

  changePeriod(event) {
    // Prevent multiple clicks during loading
    if (this.isLoading) {
      event.preventDefault()
      return
    }

    this.isLoading = true

    // Show loading state
    this.showLoading()

    // Keep spinner until page loads completely
    this.keepSpinnerUntilRedirect()
  }

  changeDate(event) {
    // Only handle date input changes
    if (event.target.type === 'date') {
      // Prevent multiple clicks during loading
      if (this.isLoading) {
        event.preventDefault()
        return
      }

      this.isLoading = true
      this.showLoading()

      // For date input changes, redirect to new date
      const selectedDate = event.target.value
      if (selectedDate) {
        const urlParams = new URLSearchParams(window.location.search)
        const period = urlParams.get('period') || 'monthly'
        const doctorId = urlParams.get('doctor_id')

        const newUrl = new URL(window.location)
        newUrl.searchParams.set('date', selectedDate)
        newUrl.searchParams.set('period', period)
        if (doctorId) {
          newUrl.searchParams.set('doctor_id', doctorId)
        }

        window.location.href = newUrl.toString()
      }
    }
  }

  handleDownload(event) {
    // Handle download links (Ver Factura)
    if (this.isLoading) {
      event.preventDefault()
      return
    }

    this.isLoading = true
    this.showLoading()

    // For downloads, we need to detect when the download dialog appears
    // We'll use a combination of focus events and timeouts
    let downloadStarted = false
    let downloadCompleted = false

    // Detect when download dialog appears (browser loses focus)
    const handleBlur = () => {
      if (!downloadStarted) {
        downloadStarted = true
      }
    }

    // Detect when user returns to the page (download completed or cancelled)
    const handleFocus = () => {
      if (downloadStarted && !downloadCompleted) {
        downloadCompleted = true
        setTimeout(() => {
          this.hideLoading()
          this.isLoading = false
        }, 1000) // Small delay to ensure download is processed
      }
    }

    // Listen for focus events
    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', handleFocus)

    // Fallback timeout in case focus events don't work
    const fallbackTimeout = setTimeout(() => {
      if (!downloadCompleted) {
        window.removeEventListener('blur', handleBlur)
        window.removeEventListener('focus', handleFocus)
        this.hideLoading()
        this.isLoading = false
      }
    }, 15000) // 15 seconds fallback

    // Clean up event listeners when download is completed
    const cleanup = () => {
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', handleFocus)
      clearTimeout(fallbackTimeout)
    }

    // Store cleanup function for potential manual cleanup
    this.downloadCleanup = cleanup
  }

  handleRedirect(event) {
    // Handle redirect links (Ver Consulta, Facturar)
    if (this.isLoading) {
      event.preventDefault()
      return
    }

    this.isLoading = true
    this.showLoading()

    // For redirects, keep spinner until page unload
    this.keepSpinnerUntilRedirect()
  }

  changeDoctor(event) {
    // Prevent multiple clicks during loading
    if (this.isLoading) {
      event.preventDefault()
      return
    }

    this.isLoading = true

    // Show loading state
    this.showLoading()

    // Get the selected doctor from the select element
    // Handle both click on button and change on select
    let doctorSelect
    if (event.target.tagName === 'SELECT') {
      // Event came from select change
      doctorSelect = event.target
    } else {
      // Event came from button click
      doctorSelect = event.target.closest('.input-group').querySelector('select[name="doctor_id"]')
    }

    const selectedDoctorId = doctorSelect ? doctorSelect.value : ''

    if (selectedDoctorId) {
      // Get current URL parameters
      const urlParams = new URLSearchParams(window.location.search)
      const period = urlParams.get('period') || 'monthly'
      const date = urlParams.get('date')

      // Build new URL with selected doctor
      const newUrl = new URL(window.location)
      newUrl.searchParams.set('doctor_id', selectedDoctorId)
      newUrl.searchParams.set('period', period)
      if (date) {
        newUrl.searchParams.set('date', date)
      }

      // Redirect to new URL
      window.location.href = newUrl.toString()
    } else {
      // If no doctor selected, hide loading after short delay
      setTimeout(() => {
        this.hideLoading()
        this.isLoading = false
      }, 1000)
    }
  }

  previousPeriod(event) {
    if (this.isLoading) {
      event.preventDefault()
      return
    }

    this.isLoading = true
    this.showLoading()

    // Get current URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const period = urlParams.get('period') || this.currentPeriodValue

    const newUrl = new URL(window.location)
    newUrl.searchParams.set('date', formatLocalDate(shiftDate(this.displayedDate(), period, -1)))
    newUrl.searchParams.set('period', period)

    // Redirect to new URL
    window.location.href = newUrl.toString()
  }

  nextPeriod(event) {
    if (this.isLoading) {
      event.preventDefault()
      return
    }

    this.isLoading = true
    this.showLoading()

    // Get current URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const period = urlParams.get('period') || this.currentPeriodValue

    const newUrl = new URL(window.location)
    newUrl.searchParams.set('date', formatLocalDate(shiftDate(this.displayedDate(), period, 1)))
    newUrl.searchParams.set('period', period)

    // Redirect to new URL
    window.location.href = newUrl.toString()
  }

  goToToday(event) {
    if (this.isLoading) {
      event.preventDefault()
      return
    }

    this.isLoading = true
    this.showLoading()

    // Get current URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const period = urlParams.get('period') || 'monthly'

    // Sin `date`, el servidor usa hoy en hora de Argentina. El reloj del navegador
    // no sirve: toISOString() da la fecha UTC, que desde las 21:00 ya es mañana.
    const newUrl = new URL(window.location)
    newUrl.searchParams.delete('date')
    newUrl.searchParams.set('period', period)

    // Redirect to new URL
    window.location.href = newUrl.toString()
  }

  // Día que muestra la pantalla, tal como lo calculó el servidor ("YYYY-MM-DD"),
  // como fecha local para que getDate/setDate no crucen de día por el huso.
  displayedDate() {
    const [year, month, day] = this.currentDateValue.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  showLoading() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('d-none')
    }
  }

  hideLoading() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('d-none')
    }
  }

  keepSpinnerUntilRedirect() {
    // Listen for page unload to hide spinner
    const handleBeforeUnload = () => {
      this.hideLoading()
      this.isLoading = false
    }

    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Also add a fallback timeout in case the redirect doesn't happen
    setTimeout(() => {
      // Remove the event listener and hide spinner as fallback
      window.removeEventListener('beforeunload', handleBeforeUnload)
      this.hideLoading()
      this.isLoading = false
    }, 10000) // 10 seconds fallback timeout
  }

  setupDateFormatter() {
    const dateInput = document.getElementById('date')
    if (!dateInput) return

    const formatDateForDisplay = () => {
      if (dateInput.value) {
        const date = new Date(dateInput.value + 'T00:00:00') // Evitar problemas de zona horaria
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()

        const span = dateInput.parentNode.querySelector('.date-display')
        if (span) {
          span.textContent = `${day}/${month}/${year}`
          dateInput.style.color = 'transparent'
        }
      }
    }

    // Formatear al cargar
    formatDateForDisplay()

    // Formatear al cambiar
    dateInput.addEventListener('change', formatDateForDisplay)
  }
}
